import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { EyeOff, EarOff, MessageSquareOff, ArrowRight, X, Quote } from "lucide-react";
import { Link } from "react-router-dom";

export default function SpørgIkke() {
  const [selectedImg, setSelectedImg] = useState(null);

  const images = [
    {
      url: "https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/47687190-0ab9-4bd1-9124-29479afee81b.jpg",
      title: "Se intet",
      icon: <EyeOff size={24} strokeWidth={1.5} />,
      desc: "Føler du ingen vil se, hvad der foregår? Mange lukker ofte øjnene for det, der gør ondt, men det er her, man skal turde kigge efter.",
      colorClass: "text-red-600",
      hoverColorClass: "group-hover:text-red-600",
      borderClass: "hover:border-red-600"
    },
    {
      url: "https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/21e84e46-4302-4669-aedb-c543fab55be8.jpg",
      title: "Hør intet",
      icon: <EarOff size={24} strokeWidth={1.5} />,
      desc: "Føler du ingen vil høre om, hvad der er sket? At blive hørt er det første skridt mod recovery og fælles forståelse.",
      colorClass: "text-emerald-600",
      hoverColorClass: "group-hover:text-emerald-600",
      borderClass: "hover:border-emerald-500"
    },
    {
      url: "https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/6f6198ff-ac9a-4a12-affb-589f0b382051.jpg",
      title: "Tal ikke",
      icon: <MessageSquareOff size={24} strokeWidth={1.5} />,
      desc: "Føler du ingen vil tale om, hvad der er sket? Tabuer og tavshed skaber ensomhed. Jeg hjælper med at bryde tavsheden.",
      colorClass: "text-amber-500",
      hoverColorClass: "group-hover:text-amber-500",
      borderClass: "hover:border-amber-400"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Spørg Ikke: Hvad er der sket med dig? – Ta' JA-Hatten På!</title>
        <meta name="description" content="Et stærkt koncept om sårbarhed, recovery og menneskeligt nærvær. Lær at skifte fokus fra 'Hvad er der galt med dig?' til 'Hvad er der sket med dig?'." />
      </Helmet>

      <section className="relative pt-32 pb-12 bg-[#fafafa] overflow-hidden">
        {/* Unique Animated Background */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          {/* Grain Texture Overlay */}
          <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
          
          {/* Floating Animated Blobs */}
          <motion.div
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -top-24 -left-24 w-[500px] h-[500px] bg-red-50 rounded-full blur-[120px] opacity-60"
          />
          <motion.div
            animate={{
              x: [0, -80, 0],
              y: [0, 100, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-1/2 -right-24 w-[600px] h-[600px] bg-slate-100 rounded-full blur-[150px] opacity-40"
          />
        </div>

        <div className="container relative z-10 mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 border-b border-red-600 text-red-600 text-xs font-bold tracking-[0.2em] uppercase mb-12"
            >
              <span>Signatur Koncept</span>
            </motion.div>
            <h1 className="text-4xl md:text-6xl font-normal text-slate-900 leading-tight mb-12 tracking-tight">
              Spørg <span className="italic font-serif text-red-600">IKKE</span>: <br />
              "Hvad er der galt med dig?"
            </h1>
            <h2 className="text-4xl md:text-6xl font-normal text-slate-900 leading-tight mb-12 tracking-tight">
              Men spørg: <br />
              "Hvad er der sket med dig?"
            </h2>
            <div className="w-24 h-px bg-slate-200 mx-auto mb-12"></div>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-light max-w-2xl mx-auto italic">
              "Man skal være psykisk stærk, for at være psykisk syg"
            </p>
          </div>

          {/* Main Narrative */}
          <div className="grid lg:grid-cols-12 gap-24 items-center mb-16">
            <div className="lg:col-span-6 space-y-12">
              <div className="prose prose-slate prose-lg max-w-none font-light text-slate-600 leading-relaxed">
                <p>
                  Jeg er generelt et løsningsorienteret menneske. Derfor arbejder jeg ud fra egne erfaringer – hvad der har virket for mig i min psykiske og fysiske recovery.
                </p>
                <p>
                  Min personlige erfaring er, at når jeg møder mennesker, som har det svært, møder jeg dem i øjenhøjde. Jeg prøver at opnå en fælles forståelse for den livssituation, de sidder i lige NU.
                </p>
                <p>
                  Jeg taler, hverken ned til, eller hen over hovedet på dem. Jeg taler <span className="italic font-serif text-slate-900">med</span> dem. Jeg spørger ind til, hvad der er sket, og ikke hvad der er galt.
                </p>
                <div className="bg-white p-8 border-l-4 border-red-600 shadow-sm my-12">
                  <p className="text-slate-900 font-medium italic mb-0">
                    "Jeg er eksperten i mit liv, og du er eksperten i dit. Sammen kan vi skabe en større og bedre forståelse."
                  </p>
                </div>
                <p>
                  Verden er omringet af alt for mange tabubelagte emner og de følelser, der ligger bag. Emner som fagfolk ofte har en uhensigtsmæssig måde at gribe an på. Ingen kan se, hvad der sker inde i mig – og jeg aner ikke, hvad der sker inde i dig.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="aspect-[4/5] md:h-[500px] md:aspect-auto overflow-hidden shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/d80a03b9-94cb-4ce8-8000-0f78ca12ed52.jpg"
                  alt="Candra Gaarde - Vejen til recovery"
                  className="w-full h-full object-cover"
                />
              </div>
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="absolute -bottom-12 -right-12 bg-white p-8 shadow-2xl max-w-sm hidden lg:block border border-slate-100"
              >
                <h4 className="text-sm font-bold tracking-widest uppercase text-red-600 mb-6">Erfaring & Overlevelse</h4>
                <ul className="space-y-4 text-slate-600 font-light italic">
                  <li>• Over 20 år som patient i psykiatrien</li>
                  <li>• Seksuelle overgreb i barndommen</li>
                  <li>• Selvmordsforsøg</li>
                  <li>• Alkohol og narkomisbrug</li>
                  <li>• Lægesjusk</li>
                  <li>• Fejldiagnoser og konsekvensen heraf</li>
                  <li>• Når "nok" er nok!</li>
                </ul>
              </motion.div>
            </div>
          </div>

          {/* Senfølger Section */}
          <div className="mt-24 lg:mt-36 mb-24 lg:mb-32">
            <div className="bg-white border border-slate-200 p-8 md:p-16 lg:p-20 relative overflow-hidden">
              {/* Decorative background element */}
              <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
              
              <div className="relative z-10 max-w-4xl mx-auto">
                <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-xs mb-4 block text-center">
                  Dybdegående Forståelse
                </span>
                <h2 className="text-3xl md:text-5xl font-serif italic text-slate-900 mb-8 leading-tight text-center">
                  Senfølger og selvmedicinering
                </h2>
                
                <div className="grid md:grid-cols-2 gap-12 items-start mt-12">
                  <div className="space-y-6 text-slate-600 leading-relaxed font-light text-base md:text-lg">
                    <p>
                      Senfølger er de fysiske, psykiske og sociale konsekvenser, der varer ved eller først viser sig mange år efter traumatiske oplevelser i barndommen eller ungdommen – som f.eks. seksuelle overgreb, svigt eller voldsom mobning.
                    </p>
                    <p>
                      En brækket arm er til at forholde sig til. Det kan man se, der er en forbinding på. Men alt det andet, er lidt for skamfuldt, besværligt, for pinligt, eller en hemmelighed vi <span className="font-semibold text-slate-900">IKKE</span> skal gå rundt med alene.
                    </p>
                    <p>
                      Ensomheden kan være så enorm og uoverskuelig, at mange får det meget værre og begynder at ”selvmedicinere” – f.eks. gennem misbrug af alkohol eller stoffer, risikabel eller grænseoverskridende sex, spiseforstyrrelser, eller direkte selvskade for at dulme den indre smerte. Der må vi ikke komme ud!
                    </p>
                  </div>
                  
                  <div className="space-y-6 text-slate-600 leading-relaxed font-light text-base md:text-lg">
                    <p>
                      Senfølger kan komme til udtryk på mange måder, herunder som Kompleks PTSD (K-PTSD), angst, depression, flashbacks og dyb mistillid til omverdenen.
                    </p>
                    <p>
                      Men det stikker også dybere ind i kroppen. Det er f.eks. forklaringen på, hvorfor en helt almindelig somatisk undersøgelse kan føles fuldstændig umulig og grænseoverskridende for en senfølgeramt.
                    </p>
                    <div className="p-6 bg-red-50 border-l-4 border-red-600 mt-8">
                      <p className="italic text-red-900 font-medium mb-0">
                        "Vi skal tale om det der er svært, og de ting vi ikke er så gode til på en travl arbejdsplads… KOMMUNIKATION!"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The Three Barriers */}
          <div className="space-y-12 lg:space-y-16">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-normal text-slate-900 mb-8 tracking-tight">De tre barrierer</h2>
              <p className="text-slate-600 font-light text-base md:text-lg">
                Føler du, at ingen vil se, høre eller tale om det, der foregår? Jeg hjælper med at bryde mønsteret sammen med jer.
              </p>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
              {images.map((img, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`group cursor-pointer p-8 md:p-12 transition-all border-t-2 border-transparent ${img.borderClass} ${
                    i === 2 ? "md:col-span-2 xl:col-span-1" : ""
                  }`}
                  onClick={() => setSelectedImg(img)}
                >
                  <div className="relative aspect-square overflow-hidden mb-10 grayscale group-hover:grayscale-0 transition-all duration-700">
                    <img
                      src={img.url}
                      alt={img.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`${img.colorClass} ${img.hoverColorClass} transition-colors`}>{img.icon}</div>
                    <h3 className={`text-2xl font-serif italic text-slate-900 ${img.hoverColorClass} transition-colors`}>{img.title}</h3>
                  </div>
                  <p className="text-slate-600 font-light leading-relaxed">{img.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12 lg:mt-16 py-16 lg:py-20 border-t border-slate-200 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl font-normal text-slate-900 mb-12 tracking-tight leading-tight">
                Bryd <span className="italic font-serif text-red-600">tavsheden</span>
              </h2>
              <p className="text-lg md:text-xl text-slate-500 mb-16 max-w-2xl mx-auto font-light whitespace-pre-line">
                Ta´ JA-Hatten På!
                Vær med til at bryde tavsheden om fordomme og tabuer! Vær nysgerrig, og få en ny bredere viden og forståelse bag om mennesket.
                <span className="text-red-600 font-bold block mt-2">Positive tanker & idéer smitter!</span>
              </p>
              <Link
                to="/kontakt"
                className="group relative inline-flex items-center justify-center px-12 py-6 bg-red-600 text-white font-semibold tracking-widest uppercase transition-all hover:bg-red-700"
              >
                <span>Book en samtale</span>
                <ArrowRight className="ml-4 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImg(null)}
            className="fixed inset-0 z-[100] bg-white/98 backdrop-blur-sm flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
          >
            <button
              onClick={() => setSelectedImg(null)}
              className="absolute top-12 right-12 text-slate-900 hover:text-red-600 transition-colors"
            >
              <X size={48} strokeWidth={1} />
            </button>
            <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="aspect-square overflow-hidden grayscale"
              >
                <img
                  src={selectedImg.url}
                  alt={selectedImg.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 20, opacity: 0 }}
                className="space-y-8"
              >
                <div className={selectedImg.colorClass}>{selectedImg.icon}</div>
                <h3 className="text-5xl md:text-7xl font-serif italic text-slate-900">{selectedImg.title}</h3>
                <p className="text-2xl text-slate-500 font-light leading-relaxed">{selectedImg.desc}</p>
                <button 
                  onClick={() => setSelectedImg(null)}
                  className="text-sm font-bold tracking-[0.3em] uppercase text-slate-400 hover:text-slate-900 transition-colors"
                >
                  Luk detaljer
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
